import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Stethoscope,
  ShieldCheck,
  ClipboardList,
  FlaskConical,
  Share2,
  Check,
  Heart,
  ScanLine,
  Droplet,
  HeartPulse,
  Activity,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SAMPLE_IDS } from "@/lib/api";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Tell us your symptoms",
    body:
      "Describe what you're experiencing — fatigue, brain fog, joint pain, headaches, GI issues. Add goals, age and context.",
  },
  {
    icon: FlaskConical,
    title: "AI drafts your workup",
    body:
      "A personalized set of labs — and imaging when clinically justified — prioritized to your symptoms and grounded in functional medicine.",
  },
  {
    icon: Share2,
    title: "Share with your practitioner",
    body:
      "Bring the workup to your next visit — a smart starting point for a real conversation about root causes.",
  },
];

const SAMPLE_META = [
  {
    id: "sample-fatigue",
    tag: "Chronic fatigue",
    title: "42-year-old woman with 6 months of fatigue & brain fog",
    tests: ["Comprehensive Thyroid Panel", "Ferritin & Iron Studies", "Salivary Cortisol Rhythm"],
    categories: ["blood", "hormonal"],
    accent: "bg-brand-blue-light text-brand-blue",
  },
  {
    id: "sample-thyroid",
    tag: "Weight gain & hair loss",
    title: "38-year-old woman with unexplained weight gain & cold hands",
    tests: ["TSH, Free T3/T4, Reverse T3", "Ferritin", "24-hour urine cortisol"],
    categories: ["blood", "urine"],
    accent: "bg-brand-green-light text-brand-green-hover",
  },
  {
    id: "sample-joints",
    tag: "Joint pain",
    title: "55-year-old man with chronic joint pain & morning stiffness",
    tests: ["hs-CRP & ESR", "RF & Anti-CCP Antibodies", "CMP"],
    categories: ["blood"],
    accent: "bg-brand-blue-light text-brand-blue",
  },
  {
    id: "sample-headache-imaging",
    tag: "Headache + red flags",
    title: "56-year-old man with new visual disturbances & headaches",
    tests: ["Brain MRI (imaging)", "CBC + CMP", "B12 & Folate"],
    categories: ["imaging", "blood"],
    accent: "bg-amber-100 text-amber-800",
    highlight: "imaging",
  },
];

const CATEGORY_CHIP = {
  blood:     { label: "Blood",     Icon: Droplet,    className: "bg-red-50 text-red-700 border-red-100" },
  urine:     { label: "Urine",     Icon: FlaskConical, className: "bg-yellow-50 text-yellow-800 border-yellow-100" },
  hormonal:  { label: "Hormonal",  Icon: HeartPulse, className: "bg-pink-50 text-pink-700 border-pink-100" },
  imaging:   { label: "Imaging",   Icon: ScanLine,   className: "bg-amber-50 text-amber-800 border-amber-200" },
  specialty: { label: "Specialty", Icon: Activity,   className: "bg-emerald-50 text-emerald-700 border-emerald-100" },
};

function CategoryChip({ category }) {
  const meta = CATEGORY_CHIP[category];
  if (!meta) return null;
  const { Icon } = meta;
  return (
    <span
      data-testid={`sample-category-chip-${category}`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${meta.className}`}
    >
      <Icon className="h-3 w-3" strokeWidth={2.4} /> {meta.label}
    </span>
  );
}

export default function Landing() {
  return (
    <>
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-20 h-80 w-80 rounded-full bg-brand-blue-light blur-3xl opacity-60" />
          <div className="absolute -top-10 right-0 h-72 w-72 rounded-full bg-brand-green-light blur-3xl opacity-60" />
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-16 md:pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-ink-600 shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-brand-blue" strokeWidth={2.5} />
              Functional medicine, meet AI
            </span>
            <h1
              data-testid="hero-headline"
              className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink-900 text-balance"
            >
              Know exactly which tests to ask your practitioner about — <span className="text-brand-blue">labs, imaging, and more</span>, prioritized for <span className="text-brand-green">your symptoms</span>.
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-6 text-lg text-ink-600 max-w-xl leading-relaxed"
            >
              Describe what you're experiencing. Our AI drafts your personalized diagnostic workup grounded in functional medicine — a smart starting point for the conversation with your doctor.
            </p>

            <ul className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
              {[
                { icon: Heart, label: "Functional medicine philosophy" },
                { icon: FlaskConical, label: "Evidence-informed" },
                { icon: ShieldCheck, label: "Not medical advice" },
              ].map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-white/70 border border-slate-100 px-3 py-2 text-sm text-ink-700"
                >
                  <Icon className="h-4 w-4 text-brand-blue" strokeWidth={2.2} />
                  {label}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/start"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-base font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5 hover:bg-brand-blue-hover active:scale-95"
              >
                Get my workup
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </Link>
              <a
                href="#samples"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-slate-200 px-6 py-3 text-base font-semibold text-ink-900 transition-transform hover:-translate-y-0.5 hover:bg-surface-subtle active:scale-95"
              >
                See sample workups
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="rounded-3xl border border-slate-100 bg-white shadow-xl p-6">
                <div className="flex items-center gap-2 text-xs text-ink-500">
                  <span className="inline-flex h-2 w-2 rounded-full bg-brand-green" />
                  Draft workup
                </div>
                <h3 className="font-heading font-semibold text-lg text-ink-900 mt-2">
                  Recommended tests
                </h3>
                <p className="text-sm text-ink-600 mt-1">
                  Prioritized by relevance to your input.
                </p>
                <ul className="mt-4 space-y-3">
                  {[
                    { name: "Comprehensive Thyroid Panel", tag: "HIGH", cat: "blood", tagColor: "text-brand-blue bg-brand-blue-light" },
                    { name: "Ferritin & Iron Studies", tag: "HIGH", cat: "blood", tagColor: "text-brand-blue bg-brand-blue-light" },
                    { name: "Salivary Cortisol Rhythm", tag: "MEDIUM", cat: "hormonal", tagColor: "text-brand-green-hover bg-brand-green-light" },
                    { name: "Fasting Insulin & HbA1c", tag: "MEDIUM", cat: "blood", tagColor: "text-brand-green-hover bg-brand-green-light" },
                  ].map((t) => (
                    <li key={t.name} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CategoryChip category={t.cat} />
                        <span className="font-medium text-ink-900 text-sm">{t.name}</span>
                      </div>
                      <span className={`text-[10px] font-bold tracking-wider rounded-full px-2 py-0.5 ${t.tagColor}`}>
                        {t.tag}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 rounded-xl bg-surface-subtle p-3 text-xs text-ink-600 leading-relaxed">
                  Discuss with your practitioner. Not medical advice.
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 hidden md:flex items-center gap-2 rounded-full bg-white border border-slate-100 px-4 py-2 shadow-lg">
                <Stethoscope className="h-4 w-4 text-brand-green" strokeWidth={2.2} />
                <span className="text-xs font-medium text-ink-900">Ready for your visit</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20 md:py-24 bg-surface-subtle border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">How it works</span>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900">
              Three steps. A better conversation with your doctor.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`how-step-${i + 1}`}
                className="rounded-2xl bg-white border border-slate-100 p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-blue-light text-brand-blue">
                    <s.icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <span className="text-xs font-bold text-ink-500">STEP {i + 1}</span>
                </div>
                <h3 className="mt-4 font-heading text-xl font-semibold text-ink-900">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE REPORTS */}
      <section id="samples" className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-hover">Sample workups</span>
              <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900">
                Real workups for common presentations
              </h2>
              <p className="mt-3 text-ink-600">
                Four fully unlocked examples — including one that shows when imaging is (and isn't) indicated.
              </p>
            </div>
          </div>

          <div data-testid="samples-grid" className="mt-12 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {SAMPLE_META.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`rounded-2xl bg-white border p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${
                  s.highlight === "imaging" ? "border-amber-200" : "border-slate-100"
                }`}
              >
                <span className={`inline-block text-[10px] font-bold tracking-wider rounded-full px-2 py-0.5 ${s.accent}`}>
                  {s.tag.toUpperCase()}
                </span>
                <h3 className="mt-3 font-heading text-lg font-semibold text-ink-900 leading-snug">
                  {s.title}
                </h3>
                <div
                  data-testid={`sample-categories-${s.id}`}
                  className="mt-3 flex flex-wrap gap-1.5"
                >
                  {s.categories.map((c) => (
                    <CategoryChip key={c} category={c} />
                  ))}
                </div>
                <ul className="mt-4 space-y-2">
                  {s.tests.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-ink-700">
                      <Check className="h-4 w-4 text-brand-green" strokeWidth={2.4} />
                      {t}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/r/${s.id}`}
                  data-testid={`sample-cta-${s.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:text-brand-blue-hover"
                >
                  See full workup
                  <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                </Link>
              </motion.div>
            ))}
          </div>

          <input type="hidden" data-testid="sample-ids" value={SAMPLE_IDS.join(",")} />
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 md:py-24 bg-surface-subtle border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center max-w-xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">Pricing</span>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900">
              Simple, transparent pricing
            </h2>
            <p className="mt-3 text-ink-600">Get a full workup, or go unlimited.</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div
              data-testid="pricing-onetime"
              className="rounded-2xl bg-white border border-slate-100 p-8 shadow-sm flex flex-col"
            >
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">One-time</span>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-bold text-ink-900">$9</span>
                <span className="text-sm text-ink-500">/ workup</span>
              </div>
              <p className="mt-3 text-sm text-ink-600">Perfect if you have a specific concern.</p>
              <ul className="mt-6 space-y-2 text-sm text-ink-700">
                {["Full AI workup unlocked", "Shareable link with practitioner", "Print-ready for your clinic visit"].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-brand-green" strokeWidth={2.4} /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/start"
                data-testid="pricing-onetime-cta"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-brand-blue px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-blue-hover active:scale-95"
              >
                Continue
              </Link>
            </div>

            <div
              data-testid="pricing-subscription"
              className="relative rounded-2xl bg-ink-900 text-white p-8 shadow-lg flex flex-col"
            >
              <span className="absolute -top-3 right-6 rounded-full bg-brand-green px-3 py-1 text-[10px] font-bold tracking-widest text-white">
                POPULAR
              </span>
              <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue-light">Unlimited</span>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-bold">$19</span>
                <span className="text-sm text-slate-300">/ month</span>
              </div>
              <p className="mt-3 text-sm text-slate-300">For ongoing optimization and re-testing.</p>
              <ul className="mt-6 space-y-2 text-sm text-slate-100">
                {[
                  "Unlimited workups",
                  "Print-ready for every clinic visit",
                  "Priority AI model",
                  "Cancel anytime",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-brand-green" strokeWidth={2.4} /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/start"
                data-testid="pricing-subscription-cta"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-ink-900 transition-transform hover:-translate-y-0.5 hover:bg-slate-100 active:scale-95"
              >
                Continue
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
